<script setup>
import { useI18n } from 'vue-i18n';
import { useOperators } from './operators';
import { replaceUnderscoreWithSpace } from './helper/filterHelper.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  filterTypes: { type: Array, default: () => [] },
  appliedFilters: { type: Array, default: () => [] },
  maxVisibleFilters: { type: Number, default: 2 },
  clearButtonLabel: { type: String, default: '' },
  moreFiltersLabel: { type: String, default: '' },
  showClearButton: { type: Boolean, default: true },
});

const emit = defineEmits(['clearFilters', 'openFilter']);

const { t } = useI18n();
const { operators } = useOperators();
const formatAttribute = key =>
  props.filterTypes.find(type => type.value === key)?.label ||
  replaceUnderscoreWithSpace(key);
const formatOperatorLabel = operator =>
  operators.value[operator]?.label || replaceUnderscoreWithSpace(operator);
const formatQueryOperator = operator =>
  operator === 'or'
    ? t('FILTER.QUERY_DROPDOWN_LABELS.OR')
    : t('FILTER.QUERY_DROPDOWN_LABELS.AND');

const formatFilterValue = value => {
  // Case 1: null, undefined, empty string
  if (!value) return '';

  // Case 2: array → map each item, use name if present, else the item itself
  if (Array.isArray(value)) {
    return value.map(item => item?.name ?? item).join(', ');
  }

  // Case 3: object with a "name" property → return name
  // Case 4: primitive (string, number, etc.) → return as is
  return value?.name ?? value;
};
</script>

<template>
  <div
    data-testid="active-filter-summary"
    class="flex flex-wrap items-center w-full gap-2 mx-auto"
  >
    <template v-for="(filter, index) in appliedFilters" :key="index">
      <div
        v-if="index < maxVisibleFilters"
        class="inline-flex min-w-0 items-center gap-2 min-h-11"
      >
        <button
          type="button"
          class="flex items-center min-h-11 min-w-0 gap-1 px-2 py-1 text-xs border rounded-xl hover:bg-n-solid-2 max-w-72 border-n-weak"
          @click="emit('openFilter')"
        >
          <span
            class="lowercase whitespace-nowrap first-letter:capitalize text-n-slate-12"
          >
            {{ formatAttribute(filter.attributeKey) }}
          </span>
          <span class="px-1 text-xs text-n-slate-10 whitespace-nowrap">
            {{ formatOperatorLabel(filter.filterOperator) }}
          </span>
          <span
            v-if="filter.values"
            :title="formatFilterValue(filter.values)"
            class="truncate text-n-slate-12"
          >
            {{ formatFilterValue(filter.values) }}
          </span>
        </button>
        <template
          v-if="
            index < maxVisibleFilters - 1 && index < appliedFilters.length - 1
          "
        >
          <span
            class="content-center h-full px-1 text-xs font-medium uppercase rounded-xl text-n-slate-10"
          >
            {{ formatQueryOperator(filter.queryOperator) }}
          </span>
        </template>
      </div>
    </template>
    <button
      v-if="appliedFilters.length > maxVisibleFilters"
      type="button"
      class="inline-flex items-center content-center px-1 text-xs rounded-xl text-n-slate-10 hover:text-n-slate-11 min-h-11"
      @click="emit('openFilter')"
    >
      {{ moreFiltersLabel }}
    </button>
    <div v-if="showClearButton" class="w-px h-3 rounded-xl bg-n-strong" />
    <Button
      v-if="showClearButton"
      :label="clearButtonLabel"
      size="xs"
      class="!px-2 !min-h-11 !rounded-xl"
      variant="ghost"
      @click="emit('clearFilters')"
    />
  </div>
</template>
